/**
 * The image CDN base, from the published payload, for `<Image>` to resize against.
 *
 * Supplied by `SiteContentProvider` from `injectedData.imagesCdnUrl`, so every
 * bespoke site mounted through site-kit gets it with no per-site wiring — the
 * same carrier `useCmsPages` rides on.
 *
 * ⚠️ SERVER AND CLIENT MUST AGREE, OR HYDRATION PATCHES THE srcset. The publish
 * SSR passes the payload as a prop and inlines the SAME object as
 * `window.__CMS_DATA__`; `imagesCdnOf` reads the prop first and the window
 * second, so both sides compute one value. A mismatch would put a `srcset` in
 * the server HTML and none in the client's first render.
 *
 * Absent (dev, standalone mirror builds with no inline payload) → `<Image>`
 * renders exactly the src it was given. Degrading to a plain image is the safe
 * direction; guessing a base is not.
 */
// `.ts` with `createElement`, not JSX: a `.tsx` exporting a component beside a hook
// and a plain function trips react-refresh/only-export-components.
import { createContext, createElement, useContext, type ReactNode } from 'react'

const ImagesCdnContext = createContext<string | undefined>(undefined)

export function ImagesCdnProvider({ base, children }: { base: string | undefined; children: ReactNode }) {
  return createElement(ImagesCdnContext.Provider, { value: base }, children)
}

export function useImagesCdnBase(): string | undefined {
  return useContext(ImagesCdnContext)
}

// `unknown`: the payload type (`InjectedCmsData`) does not declare `imagesCdnUrl`,
// and a weak `{ imagesCdnUrl?: unknown }` parameter rejects it for sharing no key.
export function imagesCdnOf(injected?: unknown): string | undefined {
  const fromProp = (injected as { imagesCdnUrl?: unknown } | null | undefined)?.imagesCdnUrl
  if (typeof fromProp === 'string' && fromProp) return fromProp
  if (typeof window === 'undefined') return undefined
  const fromWindow = (window as { __CMS_DATA__?: { imagesCdnUrl?: unknown } }).__CMS_DATA__?.imagesCdnUrl
  return typeof fromWindow === 'string' && fromWindow ? fromWindow : undefined
}
